import React, { useEffect, useState, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useBooking } from '../store/slice/bookingSlice';
import { useSalon } from '../store/slice/salonSlice';
import AdvancedFilter from '../components/AdvancedFilter';
import { ActionButton, ActionButtonGroup } from '../components/ActionControls';
import {
  bookingReferenceLabel,
  clientLabel,
  dateLabel,
  notesLabel,
  paymentModeLabel,
  paymentStatusLabel,
  salonLabel,
  serviceLabel,
  statusLabel,
  timeLabel,
  totalCostLabel,
} from '../utils/bookingDisplay';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

function bookingStatusTone(status) {
  switch ((status || '').toLowerCase()) {
    case 'completed':
    case 'confirmed':
      return 'status-pill-success';
    case 'pending':
      return 'status-pill-warning';
    case 'cancelled':
      return 'border-red-200 bg-red-50 text-red-700';
    default:
      return 'status-pill-muted';
  }
}

function paymentStatusTone(status) {
  switch ((status || '').toLowerCase()) {
    case 'paid':
      return 'status-pill-success';
    case 'pending':
      return 'status-pill-warning';
    case 'failed':
    case 'refunded':
      return 'border-red-200 bg-red-50 text-red-700';
    default:
      return 'status-pill-muted';
  }
}

function BookingsPage() {
  const {
    bookings,
    selectedBooking,
    loading,
    error,
    fetchBookings,
    fetchBookingById,
    fetchBookingsBySalon,
    updateBookingStatus,
  } = useBooking();
  const { salons, fetchSalons } = useSalon();

  const [filters, setFilters] = useState({
    search: '',
    salon: 'all',
    dateFrom: '',
    dateTo: '',
    sortBy: '',
  });
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    fetchSalons();
  }, [fetchSalons]);

  useEffect(() => {
    if (filters.salon && filters.salon !== 'all') {
      fetchBookingsBySalon(filters.salon);
    } else {
      fetchBookings();
    }
  }, [filters.salon, fetchBookings, fetchBookingsBySalon]);

  const handleFilterChange = (key, value) => {
    if (key === 'dateRange') {
      setFilters((p) => ({ ...p, dateFrom: value.from || '', dateTo: value.to || '' }));
    } else {
      setFilters((p) => ({ ...p, [key]: value }));
    }
  };

  const filterConfig = [
    { key: 'search', type: 'search', value: filters.search },
    {
      key: 'salon',
      type: 'select',
      label: 'Salon',
      value: filters.salon,
      defaultValue: 'all',
      options: salons.map((s) => ({ value: String(s.id), label: s.name })),
    },
    {
      key: 'dateRange',
      type: 'dateRange',
      label: 'Date Range',
      value: { from: filters.dateFrom, to: filters.dateTo },
    },
    {
      key: 'sortBy',
      type: 'sort',
      label: 'Sort By',
      value: filters.sortBy,
      defaultValue: '',
      options: [
        { value: 'date-asc', label: 'Date (earliest first)' },
        { value: 'date-desc', label: 'Date (latest first)' },
        { value: 'client-asc', label: 'Client (A-Z)' },
        { value: 'time-asc', label: 'Time (earliest first)' },
        { value: 'status-asc', label: 'Status (A-Z)' },
      ],
    },
  ];

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.salon && filters.salon !== 'all') count++;
    if (filters.dateFrom || filters.dateTo) count++;
    if (filters.sortBy) count++;
    return count;
  }, [filters]);

  const filteredBookings = useMemo(() => {
    let result = [...bookings];

    if (filters.search) {
      const term = filters.search.toLowerCase();
      result = result.filter((b) => {
        const blob = [
          bookingReferenceLabel(b),
          clientLabel(b),
          salonLabel(b),
          serviceLabel(b),
          notesLabel(b),
          paymentModeLabel(b),
          paymentStatusLabel(b),
        ]
          .join(' ')
          .toLowerCase();
        return blob.includes(term);
      });
    }
    if (filters.dateFrom || filters.dateTo) {
      result = result.filter((b) => {
        const dStr = dateLabel(b);
        if (!dStr) return true;
        const d = new Date(dStr);
        if (filters.dateFrom && d < new Date(filters.dateFrom)) return false;
        if (filters.dateTo && d > new Date(`${filters.dateTo}T23:59:59`)) return false;
        return true;
      });
    }
    if (filters.sortBy) {
      const [field, order] = filters.sortBy.split('-');
      result.sort((a, b) => {
        let va;
        let vb;
        if (field === 'date') {
          va = new Date(dateLabel(a) || 0);
          vb = new Date(dateLabel(b) || 0);
        } else if (field === 'time') {
          va = timeLabel(a) || '';
          vb = timeLabel(b) || '';
        } else if (field === 'status') {
          va = statusLabel(a);
          vb = statusLabel(b);
        } else {
          va = (clientLabel(a) || '').toLowerCase();
          vb = (clientLabel(b) || '').toLowerCase();
        }
        if (order === 'desc') [va, vb] = [vb, va];
        return va < vb ? -1 : va > vb ? 1 : 0;
      });
    }

    return result;
  }, [bookings, filters]);

  const handleStatusChange = async (id, status) => {
    const result = await updateBookingStatus({ id, status });
    if (result?.meta?.requestStatus === 'fulfilled') {
      toast.success('Booking status updated');
    }
  };

  const openDetail = useCallback(
    (id) => {
      setDetailId(id);
      fetchBookingById(id);
    },
    [fetchBookingById]
  );

  const closeDetail = () => setDetailId(null);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const upcomingCount = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return bookings.filter((b) => {
      const dStr = dateLabel(b);
      if (!dStr) return false;
      const d = new Date(dStr);
      return d >= today && statusLabel(b) !== 'cancelled' && statusLabel(b) !== 'completed';
    }).length;
  }, [bookings]);

  const detailBooking = selectedBooking?.id === detailId ? selectedBooking : null;

  if (loading && bookings.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-slate-500">Loading bookings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl text-slate-800 tracking-tight">Bookings</h1>
          <p className="mt-1 text-slate-500 text-sm">View and update booking status</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Total</p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{bookings.length}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500"># Filtered</p>
          <p className="mt-1 text-2xl font-bold text-amber-600">{filteredBookings.length}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Upcoming</p>
          <p className="mt-1 text-2xl font-bold text-emerald-600">{upcomingCount}</p>
        </div>
      </div>

      <AdvancedFilter
        searchPlaceholder="Search by reference, client, salon, notes..."
        filters={filterConfig}
        onFilterChange={handleFilterChange}
        activeCount={activeFilterCount}
      />

      <div className="card-premium overflow-hidden">
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/80">
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Reference</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Client</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Salon</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Service</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Date</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Time</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Amount</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Payment</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBookings.map((booking) => {
                const current = statusLabel(booking);
                const paymentStatus = paymentStatusLabel(booking);
                return (
                  <tr key={booking.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors">
                    <td className="py-4 px-6 text-slate-700 text-sm font-mono">{bookingReferenceLabel(booking) || '—'}</td>
                    <td className="py-4 px-6 font-medium text-slate-800">{clientLabel(booking)}</td>
                    <td className="py-4 px-6 text-slate-600">{salonLabel(booking) || '—'}</td>
                    <td className="py-4 px-6 text-slate-600">{serviceLabel(booking)}</td>
                    <td className="py-4 px-6 text-slate-600">{formatDate(dateLabel(booking))}</td>
                    <td className="py-4 px-6 text-slate-600">{timeLabel(booking) || '—'}</td>
                    <td className="py-4 px-6 text-slate-600">
                      {totalCostLabel(booking) !== '' ? `$${totalCostLabel(booking)}` : '—'}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex flex-col gap-1">
                        <span className={`status-pill w-fit capitalize ${paymentStatusTone(paymentStatus)}`}>
                          {paymentStatus || '—'}
                        </span>
                        {paymentModeLabel(booking) && (
                          <span className="text-xs text-slate-400 capitalize">
                            via {paymentModeLabel(booking)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`status-pill capitalize ${bookingStatusTone(current)}`}>
                        {current || '—'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <ActionButtonGroup>
                        <ActionButton
                          type="button"
                          variant="edit"
                          onClick={() => openDetail(booking.id)}
                        >
                          View
                        </ActionButton>
                        <select
                          value={STATUS_OPTIONS.some((o) => o.value === current) ? current : ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v) handleStatusChange(booking.id, v);
                          }}
                          disabled={loading}
                          className="input-premium text-sm py-2 min-w-[140px] text-left"
                          aria-label="Update booking status"
                        >
                          <option value="">Change status…</option>
                          {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value} disabled={o.value === current}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </ActionButtonGroup>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredBookings.length === 0 && (
          <div className="py-16 text-center text-slate-500">No bookings found.</div>
        )}
      </div>

      {detailId && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={closeDetail}>
          <div
            className="bg-white rounded-premium w-full max-w-lg shadow-premium-xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <h2 className="text-xl font-semibold text-slate-800">Booking Details</h2>
                <button
                  type="button"
                  onClick={closeDetail}
                  className="text-slate-400 hover:text-slate-600 text-xl leading-none"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              {loading && !detailBooking ? (
                <p className="text-slate-500 py-8 text-center">Loading booking...</p>
              ) : detailBooking ? (
                <>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    <div>
                      <dt className="text-slate-500">Reference</dt>
                      <dd className="font-mono text-slate-800">{bookingReferenceLabel(detailBooking) || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Status</dt>
                      <dd className="capitalize text-slate-800">{statusLabel(detailBooking) || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Client</dt>
                      <dd className="text-slate-800">{clientLabel(detailBooking)}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Salon</dt>
                      <dd className="text-slate-800">{salonLabel(detailBooking) || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Service</dt>
                      <dd className="text-slate-800">{serviceLabel(detailBooking)}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Amount</dt>
                      <dd className="text-slate-800">
                        {totalCostLabel(detailBooking) !== '' ? `$${totalCostLabel(detailBooking)}` : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Date</dt>
                      <dd className="text-slate-800">{formatDate(dateLabel(detailBooking))}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Time</dt>
                      <dd className="text-slate-800">{timeLabel(detailBooking) || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Payment</dt>
                      <dd className="capitalize text-slate-800">
                        {paymentStatusLabel(detailBooking) || '—'}
                        {paymentModeLabel(detailBooking) ? ` (${paymentModeLabel(detailBooking)})` : ''}
                      </dd>
                    </div>
                  </dl>
                  {notesLabel(detailBooking) && (
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Notes</p>
                      <p className="text-sm text-slate-700 bg-slate-50 rounded-premium p-3">{notesLabel(detailBooking)}</p>
                    </div>
                  )}
                  <div className="pt-2">
                    <label className="block text-sm font-medium text-slate-600 mb-1">Update status</label>
                    <select
                      value={STATUS_OPTIONS.some((o) => o.value === statusLabel(detailBooking)) ? statusLabel(detailBooking) : ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v) handleStatusChange(detailBooking.id, v);
                      }}
                      disabled={loading}
                      className="input-premium w-full text-sm"
                    >
                      <option value="">Change status…</option>
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value} disabled={o.value === statusLabel(detailBooking)}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <p className="text-slate-500 py-8 text-center">Booking not found.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BookingsPage;
